import {
    productEvent,
    type ProductJourney
} from "@shared/analytics/productEvents";
import walletIcon from "@shared/assets/icons/grayscale/wallet.svg";
import { truncateAddress } from "@shared/utils/format";
import {
    YAAS_POSITION_BADGE,
    yaasPositionBadgeState
} from "@shared/widgets/yaas/positionStatusBadge";
import { YaasPositionBalance } from "@shared/widgets/yaas/YaasPositionBalance";
import { FEATURE_FLAGS } from "@solomon-apps/config";
import { formatCurrency } from "@solomon-apps/shared";
import { ArrowShortUpRightIcon } from "@solomon-labs/design/components/icons";
import {
    Divider,
    SectionHeading
} from "@solomon-labs/design/components/layout";
import { Button, StatusBadge, Text } from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import React from "react";
import { useNavigate } from "react-router-dom";

import type { RaydiumLpHolding } from "@/hooks/yaas/lpTypes";

import type { DistributionMode } from "../application/types";
import { YaasPositionCard } from "../components/YaasPositionCard";
import { shortAddress } from "../helpers/breakdownDisplay";
import { minBalanceNotice } from "../helpers/formatMinBalance";
import type { RetailBalanceTotal } from "../helpers/retailBalanceTotal";
import type { ResolvedView } from "../state/types";

import { BalanceLoadNotice } from "./BalanceLoadNotice";
import { PositionsActionRow } from "./PositionsActionRow";
import { PositionsBreakdownList } from "./PositionsBreakdownList";

/**
 * Unified account and position summary after enrollment. The card
 * owns its border and uses a simple opacity and y entrance.
 *
 * Composes the shared `PositionsBreakdownList` and `PositionsActionRow`
 * below the card. Before a Program Position exists, show the wallet balance
 * and account destination without inventing position data or earning status.
 */
export function PositionsSection({
    resolved,
    squads = false,
    balanceAddress = resolved.address,
    primaryPosition,
    account = resolved.account,
    formattedWalletBalance,
    balanceTotal,
    balanceHoldings = [],
    balanceError = null,
    onRetryBalance,
    retryingBalance = false,
    userDisabledOverride,
    advancedMode,
    canEditDestinations,
    canSubmitDestinations,
    inlineEditPosition,
    setInlineEditPosition,
    onConfigureDestinations,
    onBuyUsdv,
    onSwapUsdv,
    onConfigureRouting,
    distributionMode,
    isBelowMinimum,
    baseApy
}: {
    resolved: ResolvedView;
    squads?: boolean;
    balanceAddress?: string;
    primaryPosition: ResolvedView["positions"][number] | null;
    account?: ResolvedView["account"];
    formattedWalletBalance: string;
    /** Wallet USDv plus LP principal, computed once by the page; null
     *  while the wallet amount or the LP observation is missing. */
    balanceTotal: RetailBalanceTotal | null;
    balanceHoldings?: RaydiumLpHolding[];
    balanceError?: Error | null;
    /** Re-runs the LP scan from the notice's Retry. */
    onRetryBalance?: () => void;
    retryingBalance?: boolean;
    userDisabledOverride: boolean;
    advancedMode: boolean;
    canEditDestinations: boolean;
    /** Whether an opened routing editor may POST. False in demo/sim. */
    canSubmitDestinations: boolean;
    inlineEditPosition: string | null;
    setInlineEditPosition: React.Dispatch<React.SetStateAction<string | null>>;
    onConfigureDestinations: (
        source: string,
        analytics?: ProductJourney
    ) => void;
    /** Card's own onBuyUSDv (below-minimum CTA). */
    onBuyUsdv: () => void;
    /** Bottom action row's "Swap USDv" CTA (opens the swap surface). */
    onSwapUsdv: () => void;
    /** "Saved Destinations" link — opens ConfigureRoutingPanel. */
    onConfigureRouting: () => void;
    /** The wallet's committed distribution mode. Only used to step the
     *  Swap CTA back when a Claim button is on screen. */
    distributionMode: DistributionMode;
    /** Derived by the page, not here: with no Program Position the rule
     *  reads the wallet balance instead, which this component cannot
     *  see. Passed in so the hero and this column agree. */
    isBelowMinimum: boolean;
    /** DRAFT — base APY quoted in the venues ghost row. Threaded from
     *  the page so the row, the footer link and the hero all quote one
     *  APY source. Omit to suppress the ghost row. */
    baseApy?: number | null;
}): React.ReactElement {
    const navigate = useNavigate();
    const isDisabledByUser = resolved.disabledByUser || userDisabledOverride;
    const badge = primaryPosition
        ? YAAS_POSITION_BADGE[
              yaasPositionBadgeState(
                  primaryPosition.position.status,
                  isBelowMinimum,
                  isDisabledByUser
              )
          ]
        : isBelowMinimum
          ? YAAS_POSITION_BADGE.error
          : YAAS_POSITION_BADGE.earning;

    // Shown beside the wallet row on the no-position card.
    const destinationAddress = account?.rewardsDestinationAddress ?? null;
    const destinationLabel =
        destinationAddress === null
            ? "Not set"
            : destinationAddress === account?.address
              ? "Same Wallet"
              : null;
    const destinationDisplay =
        destinationLabel ?? truncateAddress(destinationAddress ?? "", 4, 4);
    const destinationTitle = destinationLabel ?? destinationAddress ?? "";
    // With enrollment routing off, the card does not mention a destination
    // at all, including one saved before the flag went off.
    const showDestination = FEATURE_FLAGS.retailEnrollmentDestination;
    const formattedTotal =
        balanceTotal === null ? "—" : formatCurrency(balanceTotal.amount);
    const totalFailed = balanceError !== null && balanceTotal === null;
    const totalNotice = (
        <BalanceLoadNotice
            onRetry={onRetryBalance}
            retrying={retryingBalance}
        />
    );
    // One unreadable position leaves the rest of the total standing; the
    // line says what it leaves out, and its row shows "—".
    const partialNotice =
        balanceTotal !== null && balanceTotal.unresolved > 0 ? (
            <div className="mt-2">
                <BalanceLoadNotice
                    tone="neutral"
                    message={`Excludes ${balanceTotal.unresolved} ${balanceTotal.unresolved === 1 ? "position" : "positions"} that couldn't be loaded.`}
                    onRetry={onRetryBalance}
                    retrying={retryingBalance}
                />
            </div>
        ) : null;

    return (
        <motion.div
            key="position-card"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{
                opacity: { duration: 0.3, ease: "easeOut" },
                y: { duration: 0.3, ease: "easeOut" }
            }}
        >
            {/* Right-column page heading — sits above the position card +
                positions list. SectionHeading's `trailing` slot carries
                the status badge (Earning / Pending / Below minimum / Disabled /
                Inactive). */}
            <div className="mb-2 md:mb-4">
                <SectionHeading
                    variant="h1"
                    trailing={
                        <StatusBadge tone={badge.tone} variant="plain">
                            {badge.label}
                        </StatusBadge>
                    }
                >
                    Balances
                </SectionHeading>
            </div>
            {/* Below-minimum notice — sits directly under the heading so
                the reason is the first thing read, ahead of the balance
                it explains. The rule is a Divider element rather than a
                border on the text (layout/rule-element), and it sits
                BETWEEN the notice and the balance rather than bracketing
                either (layout/rule-edges). */}
            {isBelowMinimum && (
                <>
                    <Text
                        variant="small"
                        render={<p />}
                        className="text-error-base mb-2"
                    >
                        {minBalanceNotice(resolved.minBalance)}
                    </Text>
                    <div className="mb-4">
                        <Divider />
                    </div>
                </>
            )}
            {/* A failed read takes the total's place entirely: no
                placeholder figure, just what happened and a Retry. A
                partial sum would read as the whole balance. */}
            {primaryPosition ? (
                totalFailed ? (
                    totalNotice
                ) : (
                    <>
                        <YaasPositionCard
                            position={primaryPosition}
                            formattedTotal={formattedTotal}
                        />
                        {partialNotice}
                    </>
                )
            ) : (
                <>
                    {totalFailed ? (
                        totalNotice
                    ) : (
                        <>
                            <YaasPositionBalance
                                formatted={
                                    balanceHoldings.length ||
                                    balanceTotal === null
                                        ? formattedTotal
                                        : formattedWalletBalance
                                }
                            />
                            {partialNotice}
                        </>
                    )}
                    <div className="mt-6 flex items-center gap-3 border-y border-line-neutral px-2 py-2">
                        <img
                            src={walletIcon}
                            alt=""
                            aria-hidden
                            className="size-8 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                            <Button
                                variant="link"
                                size="sm"
                                trailing={<ArrowShortUpRightIcon />}
                                nativeButton={false}
                                role="link"
                                render={
                                    <a
                                        href={`https://solscan.io/account/${balanceAddress}`}
                                        onClick={() =>
                                            productEvent(
                                                "rewards:link_clicked",
                                                {
                                                    entry_point: "position_row",
                                                    link_type: "wallet_explorer"
                                                }
                                            )
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                }
                            >
                                {shortAddress(balanceAddress)}
                            </Button>
                            {/* The destination keeps its own label, separate
                                from the linked balance address above. */}
                            {showDestination && (
                                <Text
                                    variant="small"
                                    className="truncate text-foreground-muted"
                                    title={destinationTitle}
                                >
                                    Destination: {destinationDisplay}
                                </Text>
                            )}
                        </div>
                        <Text variant="body" className="tabular-nums">
                            {formattedWalletBalance}
                        </Text>
                    </div>
                </>
            )}
            {/* Mode 2 — per-position list sits below the position card as
                a separate surface-ground List. Each row exposes its own
                Update entry-point into the per-position routing form.
                Mode 3 surfaces the same data inside the Reward
                Destinations panel (Routing tab), so the list is
                suppressed here. A disabled position drops it too; a
                below-minimum one keeps it, because the destination is
                still live data the user may want to check while they
                top up. */}
            {(primaryPosition || balanceHoldings.length > 0) &&
                !isDisabledByUser && (
                    <PositionsBreakdownList
                        positions={resolved.positions}
                        balanceHoldings={balanceHoldings}
                        followsWalletRow={!primaryPosition}
                        connectedWalletAddress={balanceAddress}
                        advancedMode={advancedMode}
                        canEditDestinations={canEditDestinations}
                        canSubmitDestinations={canSubmitDestinations}
                        inlineEditPosition={inlineEditPosition}
                        setInlineEditPosition={setInlineEditPosition}
                        setRoutingPositionSource={onConfigureDestinations}
                        baseApy={baseApy ?? undefined}
                        onOpenVenues={
                            baseApy !== null && baseApy !== undefined
                                ? (): void => navigate("/venues")
                                : undefined
                        }
                    />
                )}
            {/* Action row — the position's one action on the left (Swap
                USDv, or Buy More USDv below the minimum), Saved
                Destinations on the right in Mode 3. Always last, so the
                column reads balance -> destinations -> action whatever
                the state. Only a disabled position has no action. */}
            {!isDisabledByUser && (
                <PositionsActionRow
                    squads={squads}
                    onSwapUsdv={onSwapUsdv}
                    belowMinimum={isBelowMinimum}
                    onBuyUsdv={onBuyUsdv}
                    deEmphasizeSwap={distributionMode === "claim"}
                    advancedMode={!!primaryPosition && advancedMode}
                    onOpenSetDestinations={onConfigureRouting}
                />
            )}
        </motion.div>
    );
}
